import { type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { PlatformBadge } from '@/components/shared/platform';
import {
  getHookContent,
  getAccountHookContent,
} from '@kirin/api-client/api/hooks';
import type { HookInventoryRow } from '@kirin/api-client/contracts/hooks';
import { getVirtualPathDetails } from '@kirin/api-client/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/hooks/api/queryKeys';
import { getHookFileDisplayLabel } from './fileLabel';
import { HOOK_STALE_TIME_MS } from './constants';
import { GitBadge } from './CellRenderers';
import { type HookTabScope, hookScopeKey } from './scope';

interface HookDetailModalProps {
  scope: HookTabScope;
  hook: HookInventoryRow | null;
  onClose: () => void;
}

/**
 * Format a Last-seen timestamp in UTC with an explicit "UTC" suffix (KIR-3643)
 * so the value is unambiguous across viewer timezones.
 */
function formatTimestampUtc(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString('en-US', { timeZone: 'UTC' })} UTC`;
}

function MetaRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): ReactElement {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-2 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words">{children}</span>
    </div>
  );
}

/**
 * Details drawer for one grouped hook row (KIR-2917). Shows the
 * type+event, platform, source file, last-seen timestamp, the generated
 * summary, and the full raw hook configuration with a copy button.
 */
export function HookDetailModal({
  scope,
  hook,
  onClose,
}: HookDetailModalProps): ReactElement {
  // `groupKey` is non-null whenever `hook` is; capture it so the query gate
  // (`enabled`) narrows the type instead of asserting inside the queryFn.
  const groupKey = hook?.groupKey ?? null;
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.hooks.content(hookScopeKey(scope), groupKey ?? ''),
    queryFn: () => {
      // `enabled` gates this to groupKey !== null; the guard narrows the type
      // without an assertion (and never throws in practice).
      if (groupKey === null) {
        throw new Error('hook content query ran without a groupKey');
      }
      return scope.kind === 'organization'
        ? getHookContent(apiClient, scope.organizationId, groupKey)
        : getAccountHookContent(apiClient, groupKey);
    },
    enabled: groupKey !== null,
    staleTime: HOOK_STALE_TIME_MS,
  });

  const isGit = hook?.sourceFamily === 'git';

  return (
    <Dialog open={hook !== null} onOpenChange={() => onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl">
        <div className="flex flex-col gap-4 border-b p-6 pb-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="flex items-center gap-2 font-mono text-base leading-relaxed">
              {hook?.fileCategory ?? '(unknown hook)'}
              {/* Git-family hooks carry a null idePlatform, so the Git badge
                  must not be gated on idePlatform; only PlatformBadge needs it. */}
              {isGit ? (
                <GitBadge />
              ) : (
                hook?.idePlatform && (
                  <PlatformBadge platform={hook.idePlatform} />
                )
              )}
            </DialogTitle>
          </DialogHeader>

          {hook && (
            <div className="flex flex-col gap-1.5">
              <MetaRow label="Hook Details">
                {data?.generatedSummary || hook.summary || (
                  <span className="text-muted-foreground">—</span>
                )}
              </MetaRow>
              <MetaRow label="Source file">
                <span className="font-mono text-xs">
                  {getHookFileDisplayLabel(hook.filePath)}
                </span>
                {getVirtualPathDetails(hook.filePath) && (
                  <span className="ml-2 font-mono text-xs text-muted-foreground">
                    ({hook.filePath})
                  </span>
                )}
              </MetaRow>
              <MetaRow label="Source type">
                {hook.hookSourceType ?? '—'}
              </MetaRow>
              <MetaRow label="Last seen">
                {formatTimestampUtc(hook.lastSeenAt)}
              </MetaRow>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-b px-6 py-3">
          <span className="text-sm font-medium">Raw configuration</span>
          {data && (
            <CopyToClipboardButton
              text={data.content}
              variant="outline"
              size="sm"
            />
          )}
        </div>

        <div className="flex-1 overflow-auto bg-muted/30 p-6">
          {error ? (
            <div className="flex flex-col items-start gap-2">
              <p className="text-sm text-destructive">
                Failed to load hook content.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
                disabled={isFetching}
              >
                {isFetching ? 'Retrying…' : 'Retry'}
              </Button>
            </div>
          ) : (
            <pre className="whitespace-pre-wrap break-all font-mono text-xs">
              {data?.content ?? (isLoading ? 'Loading…' : '(no content)')}
            </pre>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
